import React, { useEffect, useCallback } from 'react';
import { X, Minimize2, Download, RefreshCw } from 'lucide-react';

interface ExpandedPanelModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  source?: string;
  lastUpdated?: Date;
  onExport?: () => void;
  onRefresh?: () => void;
}

export const ExpandedPanelModal: React.FC<ExpandedPanelModalProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  source,
  lastUpdated,
  onExport,
  onRefresh,
}) => {
  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
      return () => {
        document.removeEventListener('keydown', handleEscape);
        document.body.style.overflow = '';
      };
    }
  }, [isOpen, onClose]);

  // Clone children with isExpanded prop
  const enhanceChildren = useCallback((children: React.ReactNode): React.ReactNode => {
    return React.Children.map(children, (child) => {
      if (React.isValidElement(child)) {
        // Clone with isExpanded prop
        return React.cloneElement(child as React.ReactElement<any>, {
          isExpanded: true,
        });
      }
      return child;
    });
  }, []);

  if (!isOpen) return null;

  const formattedDate = lastUpdated
    ? lastUpdated.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : new Date().toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/85 z-50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-3 md:inset-4 z-50 flex flex-col bg-terminal-panel border border-terminal-border rounded-lg shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="h-12 md:h-14 px-4 md:px-6 flex items-center justify-between bg-terminal-header border-b border-terminal-border shrink-0">
          <div className="flex items-center gap-4">
            {/* Live indicator */}
            <span className="w-2 h-2 rounded-full bg-data-green animate-pulse hidden md:block" />

            <div className="flex flex-col md:flex-row md:items-center md:gap-3">
              <span className="text-data-amber text-base md:text-lg font-bold uppercase tracking-wide">
                {title}
              </span>
              {subtitle && (
                <span className="text-gray-400 text-xs md:text-sm">| {subtitle}</span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-1 md:gap-2">
            {/* Refresh button */}
            {onRefresh && (
              <button
                onClick={onRefresh}
                className="flex items-center gap-1.5 px-2 md:px-3 py-1.5 text-xs text-gray-400 hover:text-white hover:bg-terminal-border rounded transition-colors"
              >
                <RefreshCw size={14} />
                <span className="hidden md:inline">Refresh</span>
              </button>
            )}

            {/* Export button */}
            {onExport && (
              <button
                onClick={onExport}
                className="flex items-center gap-1.5 px-2 md:px-3 py-1.5 text-xs text-gray-400 hover:text-white hover:bg-terminal-border rounded transition-colors"
              >
                <Download size={14} />
                <span className="hidden md:inline">Export</span>
              </button>
            )}

            {/* Minimize button */}
            <button
              onClick={onClose}
              className="flex items-center gap-1.5 px-2 md:px-3 py-1.5 text-xs text-gray-400 hover:text-white hover:bg-terminal-border rounded transition-colors"
            >
              <Minimize2 size={14} />
              <span className="hidden md:inline">Minimize</span>
            </button>

            {/* Close button */}
            <button
              onClick={onClose}
              className="p-1.5 text-gray-400 hover:text-white hover:bg-terminal-border rounded transition-colors ml-1"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Content - Enhanced with proper padding for expanded view */}
        <div className="flex-1 overflow-auto p-4 md:p-6 lg:p-8 min-h-0 bg-terminal-panel">
          {enhanceChildren(children)}
        </div>

        {/* Footer */}
        <div className="h-10 px-4 md:px-6 flex items-center justify-between bg-terminal-header border-t border-terminal-border text-xs text-gray-500 shrink-0">
          <div className="flex items-center gap-4">
            {source && (
              <span>Source: {source}</span>
            )}
            <span>Last Updated: {formattedDate}</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-gray-600">Press ESC to close</span>
            <span className="text-data-amber/60 font-medium">EXPANDED VIEW</span>
          </div>
        </div>
      </div>
    </>
  );
};

export default ExpandedPanelModal;
